/* ═══════════════════════════════════════════════════════════════
   Forma Format Bar (SYNC_PLAN §3.0 / FORMA_FORMAT_BAR_PLAN.md).

   One low-profile row under the toolbar, Forma Studio only (D5). Word's
   character controls over the ONE selection: select levels, header/footer
   tokens, End of Section — alone or mixed — and every click edits all of
   them as ONE undoable edit. The bar shows the EFFECTIVE value (the
   forma's Typography when nothing overrides it) and a mixed state when the
   selection disagrees. Nothing selected -> disabled, never hidden, so the
   layout never jumps.

   Built from the kit: Button, Input, SearchableSelect, ColorPickerPopover.
   All reading/writing is in formatBarModel.ts.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Underline,
} from 'lucide-react';
import type { StandardConfig } from '../../shared/types';
import { FONTS } from '../constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Icon } from '../components/ui/Icon';
import { Hint } from '../components/ui/tooltip';
import { ColorPickerPopover } from '../components/ui/color-picker';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  SIZE_MAX,
  SIZE_MIN,
  SPACE_MAX,
  applyBarEdit,
  readBar,
  type AlignMode,
  type BarEdit,
  type CaseMode,
  type Mixable,
} from './formatBarModel';

export const FORMAT_BAR_HINT = 'Select a level, token or End of Section to format it';
const READ_ONLY_HINT = 'View only — you need permission to edit formas to change this.';

const CASES: Array<{ mode: CaseMode; label: string; tip: string }> = [
  { mode: 'none', label: 'Aa', tip: 'Text as typed' },
  { mode: 'upper', label: 'AA', tip: 'ALL CAPS' },
  { mode: 'title', label: 'Ab', tip: 'Title Case' },
  { mode: 'sentence', label: 'ab', tip: 'Sentence case' },
];

const ALIGNS: Array<{ mode: AlignMode; icon: typeof AlignLeft; tip: string }> = [
  { mode: 'left', icon: AlignLeft, tip: 'Align left' },
  { mode: 'center', icon: AlignCenter, tip: 'Center' },
  { mode: 'right', icon: AlignRight, tip: 'Align right' },
  { mode: 'justify', icon: AlignJustify, tip: 'Justify' },
];

/** A number box that commits on blur / Enter and shows a dash when mixed. */
function NumField({
  label,
  value,
  min,
  max,
  step,
  disabled,
  title,
  onCommit,
}: {
  label: string;
  value: Mixable<number>;
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  title: string;
  onCommit: (n: number) => void;
}) {
  const shown = value.value == null ? '' : String(value.value);
  const [draft, setDraft] = React.useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const n = Number(draft);
    setDraft(null);
    if (draft.trim() === '' || !Number.isFinite(n)) return;
    const clamped = Math.min(max, Math.max(min, n));
    if (String(clamped) !== shown) onCommit(clamped);
  };
  return (
    <Hint text={title}>
      <Input
        type="number"
        aria-label={label}
        className="h-8 w-16 px-2 text-center font-mono text-xs"
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        placeholder={value.mixed ? '—' : undefined}
        value={draft ?? shown}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') setDraft(null);
        }}
      />
    </Hint>
  );
}

export interface FormatBarProps {
  forma: StandardConfig | null;
  /** The ONE selection's keys (useFormaSelection). */
  keys: string[];
  /** The Studio's undoable edit — one call per click. */
  edit: (fn: (f: StandardConfig) => StandardConfig) => void;
  canEdit: boolean;
}

export function FormatBar({ forma, keys, edit, canEdit }: FormatBarProps) {
  const r = React.useMemo(() => (forma ? readBar(forma, keys) : null), [forma, keys]);
  const none = !r || r.count === 0;
  const off = none || !canEdit;
  const apply = (e: BarEdit) => edit((f) => applyBarEdit(f, keys, e));

  // Word's rule: a toggle that is on for EVERY target turns off, anything
  // else (off, or mixed) turns on.
  const toggle = (prop: 'bold' | 'italic' | 'underline', cur: Mixable<boolean> | undefined) =>
    apply({ prop, value: cur?.value !== true });

  const fonts = React.useMemo(() => {
    const cur = r?.font.value;
    return FONTS.includes(cur ?? '') || !cur ? FONTS : [cur, ...FONTS];
  }, [r?.font.value]);

  const toggleBtn = (
    prop: 'bold' | 'italic' | 'underline',
    ic: typeof Bold,
    tip: string,
    key: Mixable<boolean> | undefined
  ) => (
    <Hint text={tip}>
      <Button
        type="button"
        size="sm"
        variant={key?.value ? 'default' : 'outline'}
        className="w-8 px-0"
        aria-label={tip}
        aria-pressed={key?.value === true}
        disabled={off}
        onClick={() => toggle(prop, key)}
      >
        <Icon icon={ic} size="md" aria-hidden />
      </Button>
    </Hint>
  );

  return (
    <div
      className="sfs-formatbar"
      role="toolbar"
      aria-label="Format the selection"
      aria-disabled={off}
    >
      <div className="sfs-formatbar-group">
        <div className="sfs-formatbar-font">
          <SearchableSelect
            value={r?.font.value ?? ''}
            onChange={(v: string) => apply({ prop: 'font', value: v })}
            options={fonts.map((f) => ({ value: f, label: f }))}
            placeholder={r?.font.mixed ? 'Mixed' : 'Font'}
            searchPlaceholder="Search fonts…"
            clearable={false}
            disabled={off}
            ariaLabel="Font"
          />
        </div>
        <NumField
          label="Font size, points"
          title="Font size (pt)"
          value={r?.size ?? { mixed: false, value: null }}
          min={SIZE_MIN}
          max={SIZE_MAX}
          step={1}
          disabled={off}
          onCommit={(n) => apply({ prop: 'size', value: n })}
        />
      </div>

      <span className="sfs-formatbar-sep" aria-hidden="true" />

      <div className="sfs-formatbar-group">
        {toggleBtn('bold', Bold, 'Bold', r?.bold)}
        {toggleBtn('italic', Italic, 'Italic', r?.italic)}
        {toggleBtn('underline', Underline, 'Underline', r?.underline)}
      </div>

      <div className="sfs-formatbar-group" role="group" aria-label="Text case">
        {CASES.map((c) => (
          <Hint key={c.mode} text={c.tip}>
            <Button
              type="button"
              size="sm"
              variant={r?.textCase.value === c.mode ? 'default' : 'outline'}
              className="w-8 px-0"
              aria-label={c.tip}
              aria-pressed={r?.textCase.value === c.mode}
              disabled={off}
              onClick={() => apply({ prop: 'textCase', value: c.mode })}
            >
              {c.label}
            </Button>
          </Hint>
        ))}
      </div>

      <Hint text="Text color">
        <ColorPickerPopover
          aria-label="Text color"
          value={`#${r?.color.value ?? '000000'}`}
          onChange={(hex) => apply({ prop: 'color', value: hex.replace('#', '') })}
          allowAlpha={false}
          disabled={off}
          triggerClassName="h-8 w-8"
        />
      </Hint>

      <span className="sfs-formatbar-sep" aria-hidden="true" />

      <div className="sfs-formatbar-group" role="group" aria-label="Alignment">
        {ALIGNS.map((a) => (
          <Hint key={a.mode} text={a.tip}>
            <Button
              type="button"
              size="sm"
              variant={r?.align.value === a.mode ? 'default' : 'outline'}
              className="w-8 px-0"
              aria-label={a.tip}
              aria-pressed={r?.align.value === a.mode}
              disabled={off}
              onClick={() => apply({ prop: 'align', value: a.mode })}
            >
              <Icon icon={a.icon} size="md" aria-hidden />
            </Button>
          </Hint>
        ))}
      </div>

      <span className="sfs-formatbar-sep" aria-hidden="true" />

      <div className="sfs-formatbar-group">
        <span className="sfs-formatbar-label">Before</span>
        <NumField
          label="Space before, points"
          title="Space before (pt)"
          value={r?.before ?? { mixed: false, value: null }}
          min={0}
          max={SPACE_MAX}
          step={1}
          disabled={off}
          onCommit={(n) => apply({ prop: 'before', value: n })}
        />
        <span className="sfs-formatbar-label">After</span>
        <NumField
          label="Space after, points"
          title="Space after (pt)"
          value={r?.after ?? { mixed: false, value: null }}
          min={0}
          max={SPACE_MAX}
          step={1}
          disabled={off}
          onCommit={(n) => apply({ prop: 'after', value: n })}
        />
        <span className="sfs-formatbar-label">Line</span>
        <NumField
          label="Line spacing"
          title="Line spacing — applies to the whole forma"
          value={r?.line ?? { mixed: false, value: null }}
          min={1}
          max={3}
          step={0.5}
          disabled={off}
          onCommit={(n) => apply({ prop: 'line', value: n })}
        />
      </div>

      <span className="sfs-formatbar-sep" aria-hidden="true" />

      <Hint text="Drop the selection's own font and size — it follows the Typography toolbox again">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={off}
          onClick={() => apply({ prop: 'reset' })}
        >
          Reset to Typography
        </Button>
      </Hint>

      {off && (
        <span className="sfs-formatbar-hint" role="status">
          {canEdit ? FORMAT_BAR_HINT : READ_ONLY_HINT}
        </span>
      )}
    </div>
  );
}
